import { useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import { MCP_SERVER_URL, type McpProvider } from "components/integrations/mcpProviders";
import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { copyToClipboard } from "lib/clipboard";
import { isPersonalEmailDomain } from "lib/emailUtils";
import {
  type CompanyInviteOutcome,
  useInviteCompanyMember,
} from "lib/hooks/api/auth/useInviteCompanyMember";
import { EventNames, useTracking } from "lib/tracking";
import { ErrorText } from "../sso/SharedStyles";

// The server's own email check, so the two can't disagree. Signup refuses personal domains.
const formSchema = z.object({
  email: z
    .string()
    .trim()
    .pipe(z.email("Enter a valid email address"))
    .refine((email) => !isPersonalEmailDomain(email), "Use their work email address"),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * What the requester pastes into Slack or Teams for their IT admin. The first step is only for
 * someone who still has an invite to accept.
 */
function nextStepsMessage(
  provider: McpProvider,
  includeAcceptInvite: boolean,
  asAdmin: boolean,
): string {
  // The generic tile names no platform, so the message says it the way a colleague would.
  const tool = provider.selfServe?.toolName ?? "our AI tool";
  const pageUrl = new URL("/account-management/integrations/mcp", window.location.origin);
  pageUrl.searchParams.set("tool", provider.id);
  const asRole = asAdmin ? " as a company admin" : "";

  const steps = [
    // Worded to fit both invite emails: register for most domains, sign-in for an SSO one.
    ...(includeAcceptInvite
      ? [`Open the Stotles invite in your inbox and follow its link to join our team${asRole}.`]
      : []),
    `In Stotles, go to Account settings → Integrations and click Connect on the "${provider.name}" tile, or open ${pageUrl.toString()}`,
    `Follow the steps on that page: generate a client ID and secret, then set up the connection in ${tool}.`,
  ];

  return [
    `Hi, could you help connect ${tool} to Stotles for our team?`,
    "",
    ...steps.map((step, index) => `${index + 1}. ${step}`),
    "",
    `Server URL: ${MCP_SERVER_URL}`,
    "",
    "Thanks!",
  ].join("\n");
}

const RESULT_TITLES: Record<CompanyInviteOutcome, string> = {
  invited: "Invite sent",
  alreadyInvited: "Already invited",
  alreadyMember: "Already on Stotles",
  accountElsewhere: "Can't send an invite",
};

function resultDescription(outcome: CompanyInviteOutcome, email: string, asAdmin: boolean): string {
  const handOver = "Send them this message so they know what to do next.";
  switch (outcome) {
    case "invited":
      return `Invite sent to ${email}. ${handOver}`;
    case "alreadyInvited":
      return `${email} has already been invited. ${handOver}`;
    case "alreadyMember":
      // An invite can't change an existing member's role.
      return asAdmin
        ? `${email} is already on Stotles. Only company admins can manage MCP credentials at your company, so make them an admin in Team management if they aren't one, then send them this message.`
        : `${email} is already on Stotles. ${handOver}`;
    case "accountElsewhere":
      return `${email} already has a Stotles account with another company, so we can't invite them to yours. Contact support or your customer success manager to move their account across, then send them this message.`;
  }
}

type Result = { email: string; outcome: CompanyInviteOutcome };

type Props = {
  /** The tile the requester arrived from, which the message names and links back to. */
  provider: McpProvider;
  /** Invite as a company admin, for a company that keeps MCP credentials to its admins. */
  asAdmin: boolean;
  onClose: () => void;
};

/**
 * Invites the requester's IT admin into the company, then hands over a message telling them what
 * to do. The person who wants the tool connected usually isn't the one who can configure it.
 */
export function InviteItAdminModal({ provider, asAdmin, onClose }: Props) {
  const { logEvent } = useTracking();
  const [result, setResult] = useState<Result | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const { mutate: invite, isLoading } = useInviteCompanyMember({
    onSuccess: (outcome, { email }) => {
      if (outcome === "invited") {
        // Team management's invite logs the same event; the context source tells the two apart.
        logEvent(EventNames.userInviteResent, {
          "Context source": "Account Management - MCP credentials",
          Provider: provider.id,
        });
      }
      setResult({ email, outcome });
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Modal open onOpenChange={onClose} maxWidth="560px" padding={24}>
      {result ? (
        <InviteResult provider={provider} result={result} asAdmin={asAdmin} onClose={onClose} />
      ) : (
        <form
          onSubmit={handleSubmit(({ email }) =>
            invite({ email, role: asAdmin ? "admin" : "member" }),
          )}
        >
          <Flex column gap={24}>
            <Flex column gap={8}>
              <Text h2>Invite your IT admin</Text>
              <Text description>
                We&apos;ll invite them to Stotles and give you a message to send them with the setup
                steps.
              </Text>
            </Flex>

            {asAdmin && (
              <Alert type="warning">
                Only company admins can manage MCP credentials at your company, so they&apos;ll join
                as a company admin. That also lets them manage your company&apos;s users and
                security settings.
              </Alert>
            )}

            <Flex column gap={6}>
              <label htmlFor="itAdminEmail">Email</label>
              <Input
                id="itAdminEmail"
                placeholder="it-admin@example.com"
                aria-label="Email"
                {...register("email")}
              />
              {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
            </Flex>

            <Flex justifyContent="flex-end" gap={8}>
              <Button type="button" buttonStyle="inverse" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} loading={isLoading}>
                Send invite
              </Button>
            </Flex>
          </Flex>
        </form>
      )}
    </Modal>
  );
}

function InviteResult({
  provider,
  result: { email, outcome },
  asAdmin,
  onClose,
}: {
  provider: McpProvider;
  result: Result;
  asAdmin: boolean;
  onClose: () => void;
}) {
  // Anyone with an account already, here or elsewhere, has no invite to accept.
  const message = nextStepsMessage(
    provider,
    outcome === "invited" || outcome === "alreadyInvited",
    asAdmin,
  );
  const description = resultDescription(outcome, email, asAdmin);

  return (
    <Flex column gap={24}>
      <Flex column gap={8}>
        <Text h2>{RESULT_TITLES[outcome]}</Text>
        {outcome === "accountElsewhere" ? (
          <Alert type="warning">{description}</Alert>
        ) : (
          <Text description>{description}</Text>
        )}
      </Flex>

      <MessageBox>{message}</MessageBox>

      <Flex justifyContent="flex-end" gap={8}>
        <Button
          buttonType="secondary"
          icon="copy01"
          onClick={async () => {
            if (await copyToClipboard({ plain: message })) {
              toast.success("Message copied to clipboard");
            } else {
              toast.error("Failed to copy message to clipboard");
            }
          }}
        >
          Copy message
        </Button>
        <Button onClick={onClose}>Done</Button>
      </Flex>
    </Flex>
  );
}

// Same frame as `CopyableField`, but in the body font: this is prose for a person, not a value.
const MessageBox = styled(Text)(({ theme }) => ({
  padding: "8px 12px",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundAlternative,
  overflowWrap: "anywhere",
}));
